<script lang="ts">
export default {
  name: 'ClientViewsModal',
};

interface Account {
  accountID: number;
  accountName: string;
  selected: boolean;
  children?: Account[];
}
</script>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from 'vue';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { X, Trash2, Check } from 'lucide-vue-next';
import { ClientView, ClientViewAccount } from '@/types/ClientView';
import { clientViewService } from '@/services/v2/ClientViewService';

defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  close: [];
  enableClientView: [];
}>();

// -----
// refs
// -----

const newClientViewName = ref('');
const accountSearch = ref('');
const clientViews = ref<ClientView[]>([]);
const accounts = ref<Account[]>([]);

// ---------
// computed
// ---------

const allAccounts = computed(() => {
  const allAccounts: Account[] = [];
  accounts.value.forEach((account) => {
    allAccounts.push(account);
    if (account.children) {
      account.children.forEach((child) => {
        allAccounts.push(child);
      });
    }
  });

  return allAccounts;
});

const searchedAccounts = computed(() => {
  if (accountSearch.value === '') {
    return accounts.value;
  }

  return accounts.value.reduce((acc: Account[], account: Account) => {
    if (account.children) {
      const children = account.children.filter((child) => {
        return child.accountName.toLowerCase().includes(accountSearch.value.toLowerCase());
      });

      if (children.length > 0 || account.accountName.toLowerCase().includes(accountSearch.value.toLowerCase())) {
        acc.push(account);
      }
    } else if (account.accountName.toLowerCase().includes(accountSearch.value.toLowerCase())) {
      acc.push(account);
    }

    return acc;
  }, [] as Account[]);
});

const selectedAccounts = computed(() => {
  return allAccounts.value.filter((a) => a.selected);
});

const ableToCreateClientView = computed(() => {
  return newClientViewName.value.trim() !== '' && selectedAccounts.value.length > 0;
});

// -----------
// lifecycles
// -----------

onBeforeMount(async () => {
  accounts.value = toAccounts(await clientViewService.getClientViewAccounts());
  clientViews.value = await clientViewService.getClientViews();
});

// ----------
// functions
// ----------

function getAccountNameById(accountID: number) {
  return allAccounts.value.find((a) => a.accountID === accountID)?.accountName;
}

function toAccounts(clientViewAccounts: ClientViewAccount[]): Account[] {
  const accountsMap = new Map<number, Account>();
  const parents: ClientViewAccount[] = [];
  const children: ClientViewAccount[] = [];
  clientViewAccounts.forEach((account) => {
    if (account.parentAccountId === null || account.parentAccountId === account.accountId) {
      parents.push(account);
    } else {
      children.push(account);
    }
  });

  parents.forEach((parent) => {
    const account = toAccount(parent);
    account.children = children.filter((child) => child.parentAccountId === parent.accountId).map(toAccount);
    accountsMap.set(account.accountID, account);

    // remove children from children array once they are added to the parent
    if (account.children.length > 0) {
      account.children.forEach((child) => {
        if (children.findIndex((c) => c.accountId === child.accountID) !== -1) {
          children.splice(
            children.findIndex((c) => c.accountId === child.accountID),
            1,
          );
        }
      });
    }
  });

  // add any remaining children to the map
  for (const child of children) {
    const account = toAccount(child);
    accountsMap.set(account.accountID, account);
  }

  return Array.from(accountsMap.values()).sort((a, b) => a.accountName.localeCompare(b.accountName));
}

function toAccount(account: ClientViewAccount): Account {
  return {
    accountID: account.accountId,
    accountName: account.accountName,
    selected: false,
  };
}

function handleAccountSelect(account: Account) {
  account.selected = !account.selected;
}

function handleAccountRemove(account: Account) {
  allAccounts.value.forEach((a) => {
    if (a.accountID === account.accountID) {
      a.selected = false;
    }
  });
}

function handleClientViewRemove(clientView: ClientView) {
  clientViewService.deleteClientView(clientView.id);
  clientViews.value = clientViews.value.filter((c) => c.id !== clientView.id);
}

function handleClearAccountSearch() {
  accountSearch.value = '';
}

function handleClearName() {
  newClientViewName.value = '';
}

async function handleCreateClientView() {
  const accountIds = selectedAccounts.value.map((a) => a.accountID);
  await clientViewService.createClientView({ name: newClientViewName.value, accountIds });
  clientViews.value = await clientViewService.getClientViews();

  // clear form
  newClientViewName.value = '';
  accountSearch.value = '';
  accounts.value = accounts.value.map((a) => {
    return { ...a, selected: false, children: a.children?.map((c) => ({ ...c, selected: false })) };
  });
}

function handleChooseClientView(clientView: ClientView) {
  emit('enableClientView');
  Commit(StoreMutationType.ENABLE_CLIENT_VIEW_MODE, {
    id: clientView.id,
    name: clientView.name,
    allowedAccountIds: clientView.accountIds,
  });
  Commit(StoreMutationType.SET_SHOW_CLIENT_VIEWS_MODAL, false);
}

function handleClose() {
  emit('close');
  // clear form
  newClientViewName.value = '';
  accountSearch.value = '';
  accounts.value = accounts.value.map((a) => {
    return { ...a, selected: false, children: a.children?.map((c) => ({ ...c, selected: false })) };
  });
  Commit(StoreMutationType.SET_SHOW_CLIENT_VIEWS_MODAL, false);
}

function handleAddRemoveAllChildren(children: Account[]) {
  const selected = children.map((c) => c.selected);
  if (selected.includes(false)) {
    children.forEach((c) => (c.selected = true));
  } else {
    children.forEach((c) => (c.selected = false));
  }
}

function addRemoveAllLabel(children: Account[]) {
  const selected = children.map((a) => a.selected);
  if (selected.includes(false)) {
    return 'Add All';
  } else {
    return 'Remove All';
  }
}
</script>

<template>
  <dialog id="client-view" class="tw:modal tw:z-2004" :class="{ 'tw:modal-open': show }">
    <div class="tw:modal-box tw:flex tw:flex-col">
      <div>
        <h3 class="tw:p-1 tw:font-normal tw:text-primary">Client Views</h3>
        <div
          v-if="clientViews.length > 0"
          class="tw:max-h-[20vh] tw:min-h-[20vh] tw:overflow-auto tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 tw:p-1">
          <table class="tw:table tw:border-collapse tw:table-sm tw:pb-10">
            <thead>
              <tr>
                <th>Name</th>
                <th>Accounts</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="clientView in clientViews"
                :key="clientView.id"
                class="tw:group tw:hover tw:transition-colors tw:hover:cursor-pointer"
                @click="handleChooseClientView(clientView)">
                <td class="tw:font-medium">{{ clientView.name }}</td>
                <td>
                  <template v-for="accountId in clientView.accountIds" :key="accountId">
                    <div
                      class="tw:badge tw:h-full tw:border tw:border-solid tw:badge-ghost tw:border-gray-500 tw:badge-sm tw:text-center">
                      {{ getAccountNameById(accountId) }}
                    </div>
                  </template>
                </td>
                <td class="tw:text-right">
                  <label
                    @click.stop="handleClientViewRemove(clientView)"
                    class="tw:group/trash tw:btn tw:bg-none tw:opacity-25 tw:btn-ghost tw:btn-sm tw:group-hover:opacity-100">
                    <Trash2 :size="15" class="tw:group-hover/trash:stroke-red-600" />
                  </label>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="tw:mt-5">
        <h3 class="tw:p-1 tw:font-normal tw:text-primary">Create a Client View</h3>
        <div class="tw:form-control tw:flex tw:flex-col">
          <label class="tw:input tw:input-sm tw:mb-2 tw:flex tw:w-1/2 tw:items-center tw:gap-2 tw:border-solid">
            <input v-model="newClientViewName" type="text" class="tw:grow" placeholder="Enter a Name" />
            <X v-if="newClientViewName.length > 0" :size="15" class="tw:cursor-pointer" @click="handleClearName" />
          </label>

          <!-- Account Search -->
          <label class="tw:input tw:input-sm tw:mb-2 tw:flex tw:w-1/2 tw:items-center tw:gap-2 tw:border-solid">
            <input type="text" v-model="accountSearch" class="tw:grow" placeholder="Account Search" />
            <X v-if="accountSearch.length > 0" :size="15" class="tw:cursor-pointer" @click="handleClearAccountSearch" />
          </label>

          <!-- Account List -->
          <ul
            class="tw:menu tw:flex tw:max-h-[20vh] tw:min-h-[20vh] tw:flex-nowrap tw:overflow-x-hidden tw:rounded-lg tw:border tw:border-solid tw:border-gray-200">
            <li v-for="account in searchedAccounts" :key="account.accountID">
              <a class="tw:flex tw:justify-between tw:text-black" @click="handleAccountSelect(account)">
                <div class="tw:flex tw:items-center tw:gap-4">
                  <span>{{ account.accountName }}</span>
                  <button
                    v-if="account.children && account.children.length > 0"
                    class="tw:btn tw:text-gray-400 tw:btn-outline tw:btn-xs tw:hover:border-gray-600 tw:hover:bg-primary/50 tw:hover:text-black"
                    @click.stop="handleAddRemoveAllChildren(account.children)">
                    {{ addRemoveAllLabel(account.children) }}
                  </button>
                </div>
                <Check v-if="account.selected" :size="16" class="tw:stroke-green-600 tw:transition-all" />
              </a>
              <ul v-if="account.children">
                <li v-for="child in account.children" :key="child.accountID">
                  <a class="tw:flex tw:justify-between tw:text-black" @click="handleAccountSelect(child)">
                    <span class="tw:whitespace-normal">{{ child.accountName }}</span>
                    <Check v-if="child.selected" :size="16" class="tw:stroke-green-600 tw:transition-all" />
                  </a>
                </li>
              </ul>
            </li>
          </ul>
          <div class="tw:flex tw:flex-wrap tw:pt-2">
            <div
              v-for="account in selectedAccounts"
              :key="account.accountID"
              @click="handleAccountRemove(account)"
              class="tw:group tw:badge tw:flex tw:h-fit tw:cursor-pointer tw:gap-1 tw:badge-ghost tw:badge-xs tw:hover:bg-red-100">
              <div class="tw:p-1">{{ account.accountName }}</div>
              <X :size="15" class="tw:group-hover:stroke-red-600" />
            </div>
          </div>
        </div>
      </div>
      <div class="tw:modal-action">
        <button class="tw:btn tw:btn-sm" @click="handleClose">Close</button>
        <button
          class="tw:btn tw:btn-sm tw:btn-primary"
          :class="{ 'tw:btn-disabled': !ableToCreateClientView }"
          @click="handleCreateClientView">
          Create
        </button>
      </div>
    </div>
  </dialog>
</template>
